import React from "react";
import { LuMail } from "react-icons/lu";
import { Button, ConfirmDialog, Dialog, Skeleton } from "../../components/ui";
import { StepsBar } from "../../components/patterns/StepsBar";
import { t } from "../../i18n";
import { EmailComposeStep } from "./EmailComposeStep";
import { EmailReviewStep } from "./EmailReviewStep";
import { Notice } from "./parts";
import { useEmailComposer } from "./useEmailComposer";

interface EmailComposerProps {
  onClose: () => void;
  /** Entra no assunto e no corpo padrão. */
  patientName?: string;
}

/** "Enviar por e-mail" em 2 passos: destinatários e anexos → revisão → envio pela conta Google. */
export const EmailComposer: React.FC<EmailComposerProps> = ({ onClose, patientName }) => {
  const s = useEmailComposer(patientName, onClose);
  const ready = s.status === "connected";

  const footer =
    s.status === "not-connected" ? (
      <>
        <Button variant="text" onClick={onClose}>
          {t("sharing.email.notNow")}
        </Button>
        <Button variant="primary" loading={s.connecting} onClick={s.connectGoogle}>
          {t("sharing.email.connect")}
        </Button>
      </>
    ) : !ready ? undefined : s.step === 1 ? (
      <>
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" onClick={s.handleContinue}>
          {t("sharing.continue")}
        </Button>
      </>
    ) : (
      <>
        <Button variant="outline" disabled={s.sending} onClick={() => s.setStep(1)}>
          {t("common.back")}
        </Button>
        <Button variant="primary" loading={s.sending} icon={<LuMail size={18} aria-hidden />} onClick={s.handleSend}>
          {t("sharing.email.send")}
        </Button>
      </>
    );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        closeDisabled={s.sending}
        title={t("sharing.email.title")}
        description={
          ready ? <StepsBar step={s.step} label={t(s.step === 1 ? "sharing.email.composeStep" : "sharing.email.reviewStep")} /> : undefined
        }
        footer={footer}
      >
        {s.status === "loading" && (
          <div role="status" className="flex flex-col gap-2.5">
            <p className="text-caption text-fg-subtle">{t("sharing.email.loading")}</p>
            {["w-[90%]", "w-[75%]", "w-[82%]"].map((w) => (
              <Skeleton key={w} className={`h-11 ${w}`} />
            ))}
          </div>
        )}

        {s.status === "error" && <p className="py-8 text-center text-caption text-fg-muted">{t("sharing.email.loadError")}</p>}

        {s.status === "not-connected" && (
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-panel bg-primary-soft text-primary" aria-hidden>
              <LuMail size={28} />
            </span>
            <p className="text-body text-fg-muted">{t("sharing.email.connectIntro")}</p>
            <Notice>{t("sharing.email.connectPermission")}</Notice>
          </div>
        )}

        {ready && (s.step === 1 ? <EmailComposeStep s={s} /> : <EmailReviewStep s={s} />)}
      </Dialog>

      {s.confirmNoReserva && (
        <ConfirmDialog
          tone="default"
          title={t("sharing.email.noReservaTitle")}
          message={t("sharing.email.noReservaMessage")}
          cancelLabel={t("sharing.email.noReservaBack")}
          confirmLabel={t("sharing.email.noReservaContinue")}
          onCancel={() => s.setConfirmNoReserva(false)}
          onConfirm={s.continueWithoutReserva}
        />
      )}
    </>
  );
};
